import { FC } from 'react';
import {
  Link,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  Typography,
  Button,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  BenefitsFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useRouteParams } from 'utils/useIdParam';
import { COLOR_BY_BENEFIT_STATUS, benefitCategories } from 'store/benefits';
import { formatCurrency } from 'utils/format';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { APP, ROUTES } from 'config';
import MultipleBenefitsPDFDownloader from 'components/benefits/BenefitsPDF/MultipleBenefitsPDFDownloader';
import { useModalContext } from 'store/modal';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  assignmentStatuses,
  benefitsByEmployeeFiltersSchema,
  useAssignBenefitsToEmployee,
  useBenefitsByEmployeeAssignments,
} from 'store/assignments';
import {
  AssignmentDateSelectModal,
  EmployeeBenefitAssignmentHistory,
} from 'containers/assignments';

export const EmployeeBenefitsAssignments: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const { openModal } = useModalContext();
  const { hasEditPermission, hasViewPermission } = useMe();
  const { currency } = useDefaultCurrency();

  const { searchParams: filters, navigate } = useSearchParams(
    benefitsByEmployeeFiltersSchema,
  );
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { data: assignments, isLoading } = useBenefitsByEmployeeAssignments(
    employeeId,
    filters,
  );

  const { mutate: assignBenefitsToEmployee, isLoading: isAssigning } =
    useAssignBenefitsToEmployee();

  return (
    <TableWrapper>
      <TableToolbar sticky>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap="10px"
        >
          <ListTotal total={assignments?.total} />

          <Stack direction="row" alignItems="center" gap="10px">
            <Filters
              value={filters}
              initialValues={benefitsByEmployeeFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              onChangeCallback={() => setSelectedRowIds({})}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  {hasViewPermission(['benefits']) ? (
                    <FilterButton
                      label={t('common.plan_name')}
                      value={value.benefits}
                      initialValue={initialValues.benefits}
                      onChange={(v) => setFilter('benefits', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <BenefitsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  ) : null}

                  <FilterButton
                    label={t('benefits.benefit_type')}
                    value={value.categories}
                    initialValue={initialValues.categories}
                    onChange={(v) => setFilter('categories', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={benefitCategories.map((category) => ({
                          key: category,
                          label: t(`menu.${category}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('alternatives.assignmentStatus')}
                    value={value.assignmentStatus}
                    initialValue={initialValues.assignmentStatus}
                    onChange={(v) => setFilter('assignmentStatus', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={assignmentStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                        searchable={false}
                        multiple={true}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={filters.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.plan_name')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <MultipleBenefitsPDFDownloader
              benefitIds={
                assignments?.items
                  .filter((benefit) => benefit.assignmentStatus !== 'off')
                  .map((benefit) => benefit.id) ?? []
              }
              noBenefitsHint={t('benefits.noAssignedBenefits')}
              hint={t('benefits.downloadAssignedBenefitsPDF')}
              fileName={t('benefits.receiverPDFFileName', {
                receiverName: employeeId,
              })}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={assignments?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.plan_name'),
            cell: ({ row: { original: assignment } }) => {
              return (
                <Link
                  label={assignment.name}
                  to={ROUTES.benefits.details.replace(':id', assignment.id)}
                />
              );
            },
            meta: { sort: true },
          },
          {
            id: 'status',
            header: t('wallet.employeeWalletBenefitStatus'),
            cell: ({ row: { original: assignment } }) => {
              return (
                <Typography
                  color={COLOR_BY_BENEFIT_STATUS[assignment.benefitStatus]}
                >
                  {t(`status.${assignment.benefitStatus}`)}
                </Typography>
              );
            },
          },
          {
            id: 'type',
            header: t('benefits.benefit_type'),
            cell: ({ row: { original: assignment } }) => {
              return t(`menu.${assignment.category}`);
            },
            meta: { sort: true },
          },
          {
            id: 'investmentAmount',
            header: t('benefits.benefit_investment'),
            cell: ({ row: { original: assignment } }) =>
              assignment.investment
                ? formatCurrency(i18n.language, assignment.investment, currency)
                : '-',
            meta: { align: 'right' },
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: assignment } }) => {
              return (
                <Stack
                  flexDirection="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    color={COLOR_BY_STATUS[assignment.assignmentStatus]}
                  >
                    {t(`status.${assignment.assignmentStatus}`)}
                  </Typography>

                  {assignment.assignmentCount ? (
                    <Button
                      variant="neutral-outline"
                      size="sm"
                      icon={AccessTime}
                      onClick={() => {
                        openModal({
                          component: EmployeeBenefitAssignmentHistory,
                          props: {
                            benefitId: assignment.id,
                            employeeId,
                            readonly: !hasEditPermission(['benefits']),
                          },
                          size: 'lg',
                        });
                      }}
                      tooltip={t('permissions.historyModule')}
                    />
                  ) : null}
                </Stack>
              );
            },
          },
        ]}
        pagination={
          assignments && {
            count: assignments.total,
            page: assignments.page,
            pageSize: assignments.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        rows={{
          inactive: ({ original: assignment }) =>
            assignment.assignmentStatus === 'off',
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: assignmentStatuses.map((status) => ({
            label: t(`status.${status}`),
            disabled: !hasEditPermission(['benefits']),
            onClick: async () => {
              const response = await openModal({
                component: AssignmentDateSelectModal,
              });

              if (response.action === 'CONFIRM') {
                assignBenefitsToEmployee(
                  {
                    employeeId,
                    ...(status === 'on'
                      ? {
                          action: 'create',
                          data: {
                            benefitIds: Object.keys(selectedRowIds),
                            ...(response.isNow
                              ? { fromNow: true }
                              : { from: response.date }),
                          },
                        }
                      : {
                          action: 'update',
                          data: {
                            benefitIds: Object.keys(selectedRowIds),
                            ...(response.isNow
                              ? { toNow: true }
                              : { to: response.date }),
                          },
                        }),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              }
            },
          })),
          isLoading: isAssigning,
        }}
      />
    </TableWrapper>
  );
};
